import { DEFAULT_COUNTRY_CODE, type CountryCode } from './country';

export type AppLanguage = 'pt' | 'es' | 'en';

export const APP_LANGUAGE_STORAGE_KEY = 'grato.app.language';
export const APP_LANGUAGE_USER_SELECTED_STORAGE_KEY =
  'grato.app.language.user-selected';
export const APP_LANGUAGE_ORDER: ReadonlyArray<AppLanguage> = [
  'pt',
  'es',
  'en',
];
export const DEFAULT_APP_LANGUAGE: AppLanguage = 'pt';
export const APP_LANGUAGE_LABEL_KEYS: Record<AppLanguage, string> = {
  en: 'login.langEn',
  es: 'login.langEs',
  pt: 'login.langPt',
};

function isSupportedAppLanguage(value: string): value is AppLanguage {
  return APP_LANGUAGE_ORDER.includes(value as AppLanguage);
}

export function resolveAppLanguage(language: string): AppLanguage {
  if (language.startsWith('es')) {
    return 'es';
  }

  if (language.startsWith('pt')) {
    return 'pt';
  }

  return 'en';
}

const API_LOCALE: Record<AppLanguage, string> = {
  pt: 'pt-BR',
  es: 'es-AR',
  en: 'en',
};

export function resolveApiLocale(language: AppLanguage): string {
  return API_LOCALE[language];
}

function resolveNavigatorAppLanguage(): AppLanguage | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const navigatorLanguages = Array.isArray(navigator.languages)
    ? navigator.languages
    : [];
  const nextLanguage = navigatorLanguages[0] ?? navigator.language;

  return typeof nextLanguage === 'string' && nextLanguage.length > 0
    ? resolveAppLanguage(nextLanguage)
    : null;
}

function resolveHtmlTagAppLanguage(): AppLanguage | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const htmlLanguage = document.documentElement.lang;

  return htmlLanguage.length > 0 ? resolveAppLanguage(htmlLanguage) : null;
}

export function resolveStoredUserSelectedAppLanguage(): AppLanguage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (
    window.localStorage.getItem(APP_LANGUAGE_USER_SELECTED_STORAGE_KEY) !== '1'
  ) {
    return null;
  }

  const storedLanguage = window.localStorage.getItem(APP_LANGUAGE_STORAGE_KEY);

  return storedLanguage !== null && isSupportedAppLanguage(storedLanguage)
    ? storedLanguage
    : null;
}

export function persistUserSelectedAppLanguage(language: AppLanguage): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, language);
  window.localStorage.setItem(APP_LANGUAGE_USER_SELECTED_STORAGE_KEY, '1');
}

export function resolveInitialAppLanguage(): AppLanguage | null {
  return (
    resolveStoredUserSelectedAppLanguage() ??
    resolveNavigatorAppLanguage() ??
    resolveHtmlTagAppLanguage()
  );
}

export function resolveCountryCodeByLanguage(language: string): CountryCode {
  const appLanguage = resolveAppLanguage(language);

  if (appLanguage === 'es') {
    return '+54';
  }

  return DEFAULT_COUNTRY_CODE;
}
